/**
 * Niche Regime Dashboard
 * ========================
 * Displays niche regime status (4 signals), stock candidates with
 * trailing stop levels, regime history timeline, and strategy recommendation.
 *
 * API: /api/niche-regime/current, /candidates, /history, /summary
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Skeleton, alpha, Chip, Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as FlatIcon,
  Speed as SpeedIcon,
  Waves as WavesIcon,
  PieChart as CrowdIcon,
  FilterDrama as DispersionIcon,
  Info as InfoIcon,
  GpsFixed as NicheIcon,
  Warning as WarningIcon,
  BarChart as ChartIcon,
  VerifiedUser as NormalIcon,
  Circle as CircleIcon,
  RadioButtonUnchecked as CircleOutlineIcon,
  Shield as ShieldIcon,
  ShowChart as ShowChartIcon,
  Groups as GroupsIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell,
} from 'recharts';
import { nicheRegimeAPI } from '../services/api';

// ── Colors ──
const C = {
  niche: '#8b5cf6',
  normal: '#64748b',
  green: '#10b981',
  red: '#ef4444',
  amber: '#f59e0b',
  blue: '#3b82f6',
  bg: '#f8fafc',
};

// ── Confidence tier colors ──
const CONFIDENCE = {
  HIGH:   { color: '#10b981', bg: '#ecfdf5', label: 'HIGH',   labelVi: 'Cao' },
  MEDIUM: { color: '#f59e0b', bg: '#fffbeb', label: 'MEDIUM', labelVi: 'TB' },
  WATCH:  { color: '#3b82f6', bg: '#eff6ff', label: 'WATCH',  labelVi: 'Theo dõi' },
};

// ── Interpretation icon map ──
const INTERP_ICONS = {
  niche:    { Icon: NicheIcon,        color: C.niche },
  warning:  { Icon: WarningIcon,      color: C.amber },
  chart:    { Icon: ChartIcon,        color: C.blue },
  decline:  { Icon: TrendingDownIcon, color: C.red },
  bounce:   { Icon: TrendingUpIcon,   color: C.amber },
  recovery: { Icon: TrendingUpIcon,   color: C.green },
  normal:   { Icon: NormalIcon,       color: C.green },
};

// ── Signal chip ──
const SignalChip = ({ active, label, icon }) => (
  <Chip
    icon={icon}
    label={label}
    size="small"
    sx={{
      fontWeight: 700,
      fontSize: '0.72rem',
      bgcolor: active ? alpha(C.green, 0.1) : alpha(C.red, 0.06),
      color: active ? C.green : '#94a3b8',
      border: `1px solid ${active ? alpha(C.green, 0.2) : '#e2e8f0'}`,
      '& .MuiChip-icon': { color: active ? C.green : '#cbd5e1', fontSize: 16 },
    }}
  />
);

// ── Stat Tile ──
const Tile = ({ label, value, sub, color, Icon }) => {
  const isLong = String(value).length > 5;
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden', height: '100%',
      borderRadius: 2.5, border: `1px solid ${alpha(color, 0.15)}`,
      bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
    }}>
      <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
          <Icon sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
          <Typography sx={{ fontSize: isLong ? '1.05rem' : '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: isLong ? 'inherit' : 'monospace' }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
        </Box>
      </Box>
    </Box>
  );
};

const TileSkeleton = () => (
  <Box sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '14px 16px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2 }} />
      <Box sx={{ flex: 1 }}><Skeleton width={70} height={12} sx={{ mb: 0.8 }} /><Skeleton width={50} height={26} /></Box>
    </Box>
  </Box>
);

const ContextTileSkeleton = () => (
  <Box sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '12px 14px', position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#f1f5f9', borderRadius: '10px 10px 0 0' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8 }}>
      <Skeleton variant="rounded" width={22} height={22} sx={{ borderRadius: 1.2 }} />
      <Skeleton width={55} height={10} />
    </Box>
    <Skeleton width={60} height={28} />
    <Skeleton width="100%" height={4} sx={{ mt: 1, borderRadius: 2 }} />
  </Box>
);

const FullPageSkeleton = () => (
  <>
    {/* 4 Stat Tiles */}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
      {[0, 1, 2, 3].map(i => <TileSkeleton key={i} />)}
    </Box>
    {/* 4 Signal Chips */}
    <Box sx={{ display: 'flex', gap: 1, mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', alignItems: 'center' }}>
      <Skeleton width={50} height={12} sx={{ mr: 1 }} />
      {[0, 1, 2, 3].map(i => <Skeleton key={i} variant="rounded" width={110} height={28} sx={{ borderRadius: 3 }} />)}
    </Box>
    {/* Interpretation Card */}
    <Box sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 2, bgcolor: '#fafbfc', border: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton width={200} height={16} />
      </Box>
      <Skeleton width="80%" height={12} sx={{ mb: 0.4 }} />
      <Skeleton width="60%" height={12} />
    </Box>
    {/* 6 Market Context tiles */}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2 }}>
      {[0, 1, 2, 3, 4, 5].map(i => <ContextTileSkeleton key={i} />)}
    </Box>
    {/* Candidates table */}
    <Box sx={{ mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box><Skeleton width={160} height={14} sx={{ mb: 0.5 }} /><Skeleton width={120} height={10} /></Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" width={60} height={18} sx={{ borderRadius: 3 }} />)}
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 0.6, borderBottom: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: CAND_GRID, gap: 1 }}>
        {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} width={i === 0 ? 20 : '80%'} height={10} />)}
      </Box>
      {[0, 1, 2, 3, 4].map(i => (
        <Box key={i} sx={{ px: 2, py: 1, borderBottom: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: CAND_GRID, gap: 1, alignItems: 'center' }}>
          <Skeleton width={16} height={14} />
          <Skeleton width={40} height={14} />
          {Array.from({ length: 10 }).map((_, j) => <Skeleton key={j} width="70%" height={12} />)}
        </Box>
      ))}
    </Box>
    {/* History charts */}
    <Box sx={{ mb: 2 }}>
      <Skeleton width={140} height={14} sx={{ mb: 1 }} />
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2, mb: 1.5 }}>
        <Skeleton width={100} height={12} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="100%" height={180} sx={{ borderRadius: 1 }} />
      </Box>
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
        <Skeleton width={120} height={12} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="100%" height={160} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
    {/* Strategy Info */}
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
      <Skeleton width={140} height={14} sx={{ mb: 1 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {[0, 1, 2, 3].map(i => (
          <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#fafbfc', border: '1px solid #f1f5f9' }}>
            <Skeleton width={80} height={12} sx={{ mb: 0.5 }} />
            <Skeleton width="90%" height={10} sx={{ mb: 0.3 }} />
            <Skeleton width="70%" height={10} />
          </Box>
        ))}
      </Box>
    </Box>
  </>
);

// ── Grid template for candidate table ──
const CAND_GRID = '40px 65px 1.2fr 1fr 1fr 1fr 1fr 1fr 0.8fr 0.7fr 0.8fr 1.5fr';

// ── Column header with tooltip ──
const ColHeader = ({ label, tip }) => (
  <Tooltip title={tip || ''} placement="top" arrow disableHoverListener={!tip}>
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.3px', cursor: tip ? 'help' : 'default' }}>
      {label}
    </Typography>
  </Tooltip>
);

// ── Candidate table header ──
const CandidateHeader = ({ t }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: CAND_GRID, gap: 1, alignItems: 'center',
    px: 2, py: 0.6, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc',
  }}>
    <ColHeader label="#" />
    <ColHeader label={t('nicheRegime.colTicker')} />
    <ColHeader label={t('nicheRegime.colPrice')} tip={t('nicheRegime.tipPrice')} />
    <ColHeader label={t('nicheRegime.colRet20')} tip={t('nicheRegime.tipRet20')} />
    <ColHeader label={t('nicheRegime.colRS')} tip={t('nicheRegime.tipRS')} />
    <ColHeader label={t('nicheRegime.colVolBuy')} tip={t('nicheRegime.tipVolBuy')} />
    <ColHeader label={t('nicheRegime.colLiq')} tip={t('nicheRegime.tipLiq')} />
    <ColHeader label={t('nicheRegime.colOBV')} tip={t('nicheRegime.tipOBV')} />
    <ColHeader label={t('nicheRegime.colScore')} tip={t('nicheRegime.tipScore')} />
    <ColHeader label={t('nicheRegime.colConfidence', 'Tin cậy')} tip={t('nicheRegime.tipConfidence', 'Mức độ tin cậy dựa trên score thị trường: HIGH (4/4), MEDIUM (3/4), WATCH (2/4). Score thấp hơn → stop-loss chặt hơn')} />
    <ColHeader label={t('nicheRegime.colSafety', 'An toàn')} tip={t('nicheRegime.tipSafety', 'Chỉ số an toàn: RSI(14), giá cách MA20, số phiên tăng liên tiếp. Càng thấp càng tốt = cổ phiếu chưa nóng')} />
    <ColHeader label={t('nicheRegime.colStopLoss')} tip={t('nicheRegime.tipStopLoss')} />
  </Box>
);

// ── Candidate row with decision-friendly colors ──
const CandidateRow = ({ c, rank, maxRankScore, t }) => {
  const latestPrice = c.close;
  const ret20 = c.latest_ret_20 != null ? (c.latest_ret_20 * 100).toFixed(1)
    : c.stk_ret_20 != null ? (c.stk_ret_20 * 100).toFixed(1) : null;
  const excessRs = c.excess_rs != null ? (c.excess_rs * 100).toFixed(1) : null;
  const volImb = c.vol_imbalance != null ? (c.vol_imbalance * 100).toFixed(0) : null;
  const medVal = c.median_value_20 != null ? (c.median_value_20 / 1e9).toFixed(1) : null;
  const obvSlope = c.obv_slope;
  const isTop3 = rank <= 3;
  const scorePct = c.rank_score != null && maxRankScore > 0 ? c.rank_score / maxRankScore : 0;
  const scoreColor = scorePct >= 0.75 ? C.green : scorePct >= 0.4 ? C.amber : C.red;
  const conf = CONFIDENCE[c.confidence] || CONFIDENCE.WATCH;

  // Safety score: composite 0-100 (lower = safer)
  const rsi = c.rsi_14 || 0;
  const ext = (c.price_ext || 0) * 100;
  const streak = c.consec_up || 0;
  const ret5d = (c.ret_5d || 0) * 100;
  const volSpike = c.vol_spike || 0;
  // Heat: RSI 25%, Extension 25%, Ret5d 25%, VolSpike 15%, Streak 10%
  const heatRsi = Math.max(0, (rsi - 30) / 50) * 25;          // 30-80 RSI mapped to 0-25
  const heatExt = Math.max(0, ext / 12) * 25;                  // 0-12% ext mapped to 0-25
  const heatRet5d = Math.max(0, ret5d / 15) * 25;              // 0-15% ret5d mapped to 0-25
  const heatVolSpike = Math.min(volSpike / 8, 1) * 15;         // 0-8x spike mapped to 0-15
  const heatStreak = Math.min(streak / 7, 1) * 10;             // 0-7 streak mapped to 0-10
  const heatScore = Math.min(100, Math.round(heatRsi + heatExt + heatRet5d + heatVolSpike + heatStreak));
  const safetyColor = heatScore <= 40 ? C.green : heatScore <= 70 ? C.amber : C.red;
  const safetyLabel = heatScore <= 40 ? t('nicheRegime.safetyCool', 'Mát')
    : heatScore <= 70 ? t('nicheRegime.safetyWarm', 'Ấm')
    : t('nicheRegime.safetyHot', 'Nóng');
  const safetyTip = `RSI: ${rsi.toFixed(0)} | Ext: +${ext.toFixed(1)}% | Ret5d: ${ret5d >= 0 ? '+' : ''}${ret5d.toFixed(1)}% | Vol: ${volSpike.toFixed(1)}× | Streak: ${streak}`;

  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: CAND_GRID, gap: 1, alignItems: 'center',
      px: 2, py: 1, borderBottom: '1px solid #f1f5f9',
      borderLeft: isTop3 ? `3px solid ${conf.color}` : '3px solid transparent',
      bgcolor: isTop3 ? alpha(conf.color, 0.02) : '#fff',
      '&:hover': { bgcolor: alpha(conf.color, 0.04) },
      transition: 'background 0.15s',
    }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: isTop3 ? conf.color : '#94a3b8' }}>#{rank}</Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#0f172a', fontFamily: 'monospace' }}>{c.ticker}</Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
        {latestPrice != null ? Number(latestPrice).toLocaleString('vi-VN') : '–'}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: ret20 != null && Number(ret20) >= 0 ? C.green : C.red }}>
        {ret20 != null ? `${Number(ret20) >= 0 ? '+' : ''}${ret20}%` : '–'}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: C.green }}>
        {excessRs != null ? `+${excessRs}%` : '–'}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: volImb != null && Number(volImb) >= 60 ? C.green : C.blue }}>
        {volImb != null ? `${volImb}%` : '–'}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, fontFamily: 'monospace', color: '#64748b' }}>
        {medVal != null ? `${medVal}B` : '–'}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, fontFamily: 'monospace', color: obvSlope != null && obvSlope > 0 ? C.green : C.red }}>
        {obvSlope != null ? `${obvSlope > 0 ? '+' : ''}${(obvSlope / 1e3).toFixed(0)}K` : '–'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: scoreColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, fontFamily: 'monospace', color: scoreColor }}>
          {c.rank_score != null ? c.rank_score.toFixed(1) : '–'}
        </Typography>
      </Box>
      <Chip
        label={conf.labelVi}
        size="small"
        sx={{
          fontSize: '0.6rem', fontWeight: 800, height: 20, minWidth: 44,
          bgcolor: alpha(conf.color, 0.1), color: conf.color,
          border: `1px solid ${alpha(conf.color, 0.25)}`,
          '& .MuiChip-label': { px: 0.8 },
        }}
      />
      <Tooltip title={safetyTip} placement="top" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'help' }}>
          <Box sx={{
            width: 28, height: 6, borderRadius: 3, bgcolor: alpha(safetyColor, 0.15),
            position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{
              width: `${heatScore}%`, height: '100%', borderRadius: 3,
              bgcolor: safetyColor, transition: 'width 0.3s',
            }} />
          </Box>
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: safetyColor }}>
            {safetyLabel}
          </Typography>
        </Box>
      </Tooltip>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <ShieldIcon sx={{ fontSize: 12, color: C.red }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: C.red }}>
            {c.stop_price != null ? c.stop_price.toLocaleString('vi-VN') : '–'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
          {c.stop_loss_pct != null ? `-${(c.stop_loss_pct * 100).toFixed(1)}%` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Custom tooltip for chart ──
const ChartTooltip = ({ active, payload, label, t }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 180 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.68rem', color: d.regime_niche ? C.niche : '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {d.regime_niche
          ? <><CircleIcon sx={{ fontSize: 10, color: C.niche }} /> {t('nicheRegime.tooltipNiche')}</>
          : <><CircleOutlineIcon sx={{ fontSize: 10, color: '#94a3b8' }} /> {t('nicheRegime.tooltipNormal')}</>}
        {' — '}{t('nicheRegime.tooltipScore', { score: d.niche_score })}
      </Typography>
      {d.dispersion_20 != null && (
        <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>
          {t('nicheRegime.tooltipDispersion', { value: (d.dispersion_20 * 100).toFixed(2) })}
        </Typography>
      )}
    </Box>
  );
};

// ══════════════════════════════════════
function NicheRegimeDashboard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [regime, setRegime] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [history, setHistory] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [regimeRes, candsRes, histRes] = await Promise.all([
        nicheRegimeAPI.getCurrent(),
        nicheRegimeAPI.getCandidates(),
        nicheRegimeAPI.getHistory(90),
      ]);

      setRegime(regimeRes.data);
      setCandidates(candsRes.data);

      // Format history for chart
      if (histRes.data?.data) {
        const hist = histRes.data.data.map(d => ({
          ...d,
          date: d.trade_date,
          score: d.niche_score,
          disp: d.dispersion_20 != null ? +(d.dispersion_20 * 100).toFixed(2) : null,
        })).reverse();
        setHistory(hist);
      }
      if (histRes.data?.summary) {
        setHistorySummary(histRes.data.summary);
      }
    } catch (err) {
      setError(err.message || t('nicheRegime.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCalculate = useCallback(async () => {
    try {
      setCalculating(true);
      setError(null);
      await nicheRegimeAPI.calculate();
      await fetchData();
    } catch (err) {
      setError(err.message || 'Calculate failed');
    } finally {
      setCalculating(false);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const isNiche = regime?.regime_niche === true;
  const score = regime?.niche_score ?? 0;
  const interpIcon = regime?.interpretation?.icon || 'normal';
  // Map interpretation icon → tile color, label key, MUI icon
  const REGIME_TILE = {
    niche:    { color: C.niche,  labelKey: 'nicheRegime.regimeNiche',    Icon: NicheIcon },
    warning:  { color: C.amber,  labelKey: 'nicheRegime.regimeNear',     Icon: WarningIcon },
    chart:    { color: C.blue,   labelKey: 'nicheRegime.regimeWatch',    Icon: ChartIcon },
    decline:  { color: C.red,    labelKey: 'nicheRegime.regimeDecline',  Icon: TrendingDownIcon },
    bounce:   { color: C.amber,  labelKey: 'nicheRegime.regimeBounce',   Icon: TrendingUpIcon },
    recovery: { color: C.green,  labelKey: 'nicheRegime.regimeRecovery', Icon: TrendingUpIcon },
    normal:   { color: C.green,  labelKey: 'nicheRegime.regimeNormal',   Icon: NormalIcon },
  };
  const regimeTile = REGIME_TILE[interpIcon] || REGIME_TILE.normal;
  const regimeColor = regimeTile.color;
  const candsList = candidates?.candidates || [];
  const strategy = candidates?.strategy;
  const interpretation = regime?.interpretation;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 2, md: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: '12px', flexShrink: 0,
            background: `linear-gradient(135deg, ${C.niche} 0%, ${C.blue} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha(C.niche, 0.25)}`,
          }}>
            <DispersionIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
                {t('nicheRegime.title')}
              </Typography>
              {regime?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.niche, boxShadow: `0 0 6px ${C.niche}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {regime.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('nicheRegime.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small"
            startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon sx={{ fontSize: 16 }} />}
            onClick={handleCalculate} disabled={calculating || loading}
            sx={{
              borderRadius: '4px', borderColor: alpha(C.niche, 0.3), color: C.niche,
              fontWeight: 600, fontSize: 13, textTransform: 'none',
              '&:hover': { borderColor: C.niche, backgroundColor: alpha(C.niche, 0.06) },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}>
            {t('common.calculate', 'Tính toán')}
          </Button>
          <Button variant="outlined" size="small"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={fetchData} disabled={loading}
            sx={{
              borderRadius: '4px', borderColor: '#e2e8f0', color: '#64748b',
              fontWeight: 600, fontSize: 13, textTransform: 'none',
              '&:hover': { borderColor: C.niche, color: C.niche, backgroundColor: alpha(C.niche, 0.04) },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}>
            {t('common.refresh', 'Làm mới')}
          </Button>
        </Box>
      </Box>

      {/* ── Error ── */}
      {error && !regime && (
        <Box sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 1.5, bgcolor: alpha(C.red, 0.06), border: `1px solid ${alpha(C.red, 0.15)}` }}>
          <Typography sx={{ fontSize: '0.8rem', color: C.red }}>{error}</Typography>
        </Box>
      )}

      {/* ── Stat Tiles ── */}
      {loading && !regime ? (
        <FullPageSkeleton />
      ) : regime && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            <Tile
              label={t('nicheRegime.regime')}
              value={t(regimeTile.labelKey)}
              sub={regime.trade_date}
              color={regimeColor}
              Icon={regimeTile.Icon}
            />
            <Tile
              label={t('nicheRegime.score')}
              value={`${score}/4`}
              sub={score >= 4 ? t('nicheRegime.scoreFull') : score >= 3 ? t('nicheRegime.scoreNear') : interpIcon === 'decline' ? t('nicheRegime.scoreDecline') : t('nicheRegime.scoreNormal')}
              color={score >= 4 ? C.niche : score >= 3 ? C.amber : interpIcon === 'decline' ? C.red : C.normal}
              Icon={SpeedIcon}
            />
            <Tile
              label={t('nicheRegime.candidates')}
              value={candsList.length}
              sub={candsList.length > 0 ? t('nicheRegime.candidatesTop', { ticker: candsList[0]?.ticker }) : t('nicheRegime.candidatesNone')}
              color={candsList.length > 0 ? C.green : C.normal}
              Icon={TrendingUpIcon}
            />
            <Tile
              label={t('nicheRegime.dispersion')}
              value={regime.dispersion_20 != null ? `${(regime.dispersion_20 * 100).toFixed(1)}%` : '–'}
              sub={regime.high_dispersion ? t('nicheRegime.dispersionHigh') : t('nicheRegime.dispersionNormal')}
              color={regime.high_dispersion ? C.niche : C.normal}
              Icon={DispersionIcon}
            />
          </Box>

          {/* ── 4 Signals ── */}
          <Box sx={{
            display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap',
            p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0',
          }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', mr: 1, alignSelf: 'center' }}>
              {t('nicheRegime.signals')}:
            </Typography>
            <SignalChip active={regime.index_flat} label={t('nicheRegime.signalIndexFlat')} icon={<FlatIcon />} />
            <SignalChip active={regime.high_dispersion} label={t('nicheRegime.signalHighDispersion')} icon={<DispersionIcon />} />
            <SignalChip active={regime.low_participation} label={t('nicheRegime.signalLowParticipation')} icon={<WavesIcon />} />
            <SignalChip active={regime.crowded_top} label={t('nicheRegime.signalCrowdedTop')} icon={<CrowdIcon />} />
            <Box sx={{ flex: 1 }} />
            <Tooltip title={t('nicheRegime.signalFormula')}>
              <InfoIcon sx={{ fontSize: 16, color: '#cbd5e1', cursor: 'help' }} />
            </Tooltip>
          </Box>

          {/* ── Interpretation Card (always show) ── */}
          {interpretation && (
            <Box sx={{
              px: 2, py: 1.5, mb: 2, borderRadius: 2,
              bgcolor: isNiche ? alpha(C.niche, 0.06) : alpha(C.blue, 0.04),
              border: `1px solid ${isNiche ? alpha(C.niche, 0.15) : alpha(C.blue, 0.12)}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                {(() => {
                  const interp = INTERP_ICONS[interpretation.icon] || INTERP_ICONS.normal;
                  return <interp.Icon sx={{ fontSize: 18, color: interp.color }} />;
                })()}
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: isNiche ? C.niche : '#334155' }}>
                  {interpretation.headline}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.6 }}>
                {interpretation.action}
              </Typography>
              {interpretation.signals_vi?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.8, flexWrap: 'wrap' }}>
                  {interpretation.signals_vi.map((s, i) => (
                    <Chip key={i} label={s} size="small" sx={{
                      fontSize: '0.65rem', fontWeight: 600, height: 22,
                      bgcolor: alpha(C.niche, 0.08), color: C.niche, border: `1px solid ${alpha(C.niche, 0.15)}`,
                    }} />
                  ))}
                </Box>
              )}
              {isNiche && strategy && (
                <Box sx={{ display: 'flex', gap: 2, mt: 0.8, pt: 0.8, borderTop: `1px solid ${alpha(C.niche, 0.1)}` }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    {t('nicheRegime.strategyLabel')}: <strong style={{ color: C.niche }}>{strategy.description}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    Sharpe: <strong style={{ color: C.green }}>{strategy.backtest_sharpe}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    Alpha: <strong style={{ color: C.green }}>{strategy.backtest_alpha}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    Win: <strong style={{ color: C.green }}>{strategy.backtest_win_vs_index}</strong>
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Market Context ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              { label: t('nicheRegime.ctxIdxRet20'), value: regime.idx_ret_20, fmt: 'pct', color: v => v >= 0 ? C.green : C.red, Icon: v => v >= 0 ? TrendingUpIcon : TrendingDownIcon, tip: 'Biến động VN-Index 20 phiên' },
              { label: t('nicheRegime.ctxIdxRet60'), value: regime.idx_ret_60, fmt: 'pct', color: v => v >= 0 ? C.green : C.red, Icon: v => v >= 0 ? TrendingUpIcon : TrendingDownIcon, tip: 'Biến động VN-Index 60 phiên' },
              { label: t('nicheRegime.ctxADX'), value: regime.idx_adx_14, fmt: 'num1', color: v => v < 20 ? C.amber : C.green, Icon: () => SpeedIcon, tip: 'ADX < 20 = thị trường đi ngang', gauge: { min: 0, max: 50, threshold: 20 } },
              { label: t('nicheRegime.ctxBreadth'), value: regime.pct_above_ma20, fmt: 'pct', color: v => v < 0.4 ? C.red : C.green, Icon: () => GroupsIcon, tip: '% cổ phiếu trên MA20', gauge: { min: 0, max: 1, threshold: 0.4 } },
              { label: t('nicheRegime.ctxAdvRatio'), value: regime.adv_ratio, fmt: 'pct', color: v => v < 0.4 ? C.red : C.green, Icon: () => ChartIcon, tip: 'Tỷ lệ tăng/giảm', gauge: { min: 0, max: 1, threshold: 0.4 } },
              { label: t('nicheRegime.ctxTopTurnover'), value: regime.top_n_turnover_share, fmt: 'pct', color: v => v > 0.5 ? C.amber : C.green, Icon: () => FireIcon, tip: 'Dòng tiền tập trung vào top', gauge: { min: 0, max: 1, threshold: 0.6 } },
            ].map(({ label, value, fmt, color, Icon, tip, gauge }) => {
              const v = value != null ? Number(value) : null;
              let display = '–';
              if (v != null) {
                if (fmt === 'pct') display = `${(v * 100).toFixed(1)}%`;
                else if (fmt === 'num1') display = v.toFixed(1);
              }
              const clr = v != null ? color(v) : '#94a3b8';
              const IconComp = v != null ? Icon(v) : ShowChartIcon;
              const pct = gauge && v != null ? Math.min(Math.max((v - gauge.min) / (gauge.max - gauge.min), 0), 1) * 100 : null;
              return (
                <Tooltip key={label} title={tip || ''} placement="top" arrow>
                  <Box sx={{
                    position: 'relative', overflow: 'hidden',
                    borderRadius: 2.5, bgcolor: '#fff',
                    border: `1px solid ${alpha(clr, 0.15)}`,
                    p: '12px 14px', cursor: 'default',
                    transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
                    '&:hover': {
                      borderColor: alpha(clr, 0.35),
                      boxShadow: `0 6px 20px ${alpha(clr, 0.12)}`,
                      transform: 'translateY(-2px)',
                    },
                  }}>
                    {/* Gradient accent */}
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                      background: `linear-gradient(90deg, ${clr} 0%, ${alpha(clr, 0.3)} 100%)`,
                      borderRadius: '10px 10px 0 0',
                    }} />
                    {/* Background glow */}
                    <Box sx={{
                      position: 'absolute', top: -10, right: -10, width: 60, height: 60,
                      background: `radial-gradient(circle, ${alpha(clr, 0.06)} 0%, transparent 70%)`,
                    }} />
                    {/* Icon + Label row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8, position: 'relative' }}>
                      <Box sx={{
                        width: 22, height: 22, borderRadius: 1.2, flexShrink: 0,
                        background: `linear-gradient(135deg, ${alpha(clr, 0.15)} 0%, ${alpha(clr, 0.05)} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <IconComp sx={{ fontSize: 13, color: clr }} />
                      </Box>
                      <Typography sx={{
                        fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                        color: '#94a3b8', letterSpacing: '0.4px', lineHeight: 1,
                      }}>
                        {label}
                      </Typography>
                    </Box>
                    {/* Value */}
                    <Typography sx={{
                      fontSize: '1.35rem', fontWeight: 900, color: clr,
                      fontFamily: 'monospace', lineHeight: 1.1, position: 'relative',
                      mb: gauge ? 1 : 0,
                    }}>
                      {display}
                    </Typography>
                    {/* Mini gauge bar */}
                    {gauge && v != null && (
                      <Box sx={{ position: 'relative', mt: 0.5 }}>
                        <Box sx={{
                          width: '100%', height: 4, borderRadius: 2,
                          bgcolor: alpha(clr, 0.08),
                        }}>
                          <Box sx={{
                            width: `${pct}%`, height: '100%', borderRadius: 2,
                            background: `linear-gradient(90deg, ${alpha(clr, 0.5)}, ${clr})`,
                            transition: 'width 0.6s ease',
                          }} />
                        </Box>
                        {/* Threshold marker */}
                        <Box sx={{
                          position: 'absolute',
                          left: `${((gauge.threshold - gauge.min) / (gauge.max - gauge.min)) * 100}%`,
                          top: -1, width: '1.5px', height: 6,
                          bgcolor: '#94a3b8', borderRadius: 1,
                          transform: 'translateX(-50%)',
                        }} />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      {/* ── Candidates List ── */}
      {candsList.length > 0 && (() => {
        const maxRankScore = Math.max(...candsList.map(c => c.rank_score || 0), 0.01);
        // Signal age: use latest trading date (not calendar) to avoid weekend inflation
        const signalDate = candsList[0]?.trade_date;
        const latestPriceDate = candidates?.latest_price_date;
        const refDate = latestPriceDate ? new Date(latestPriceDate) : new Date();
        const signalAgeDays = signalDate ? Math.round((refDate - new Date(signalDate)) / 86400000) : 0;
        const regimeActive = candidates?.regime_niche === true;
        const isExpired = signalAgeDays > 5;  // max hold = 5 days
        const isRegimeInactive = !regimeActive && isExpired;
        const borderColor = isExpired ? (isRegimeInactive ? alpha('#94a3b8', 0.3) : alpha(C.red, 0.3)) : '#e2e8f0';
        return (
        <Box sx={{ mb: 2, borderRadius: 2, border: `1px solid ${borderColor}`, bgcolor: '#fff', overflow: 'hidden', overflowX: 'auto' }}>
          {/* Expired / Regime-inactive banner */}
          {isExpired && (
            <Box sx={{
              px: 2, py: 1.2,
              bgcolor: isRegimeInactive ? alpha('#94a3b8', 0.06) : alpha(C.red, 0.06),
              borderBottom: `1px solid ${isRegimeInactive ? alpha('#94a3b8', 0.12) : alpha(C.red, 0.12)}`,
              display: 'flex', alignItems: 'flex-start', gap: 1,
            }}>
              <WarningIcon sx={{ fontSize: 18, color: isRegimeInactive ? '#94a3b8' : C.red, mt: 0.2 }} />
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: isRegimeInactive ? '#64748b' : C.red, letterSpacing: '0.5px' }}>
                  {isRegimeInactive
                    ? t('nicheRegime.regimeInactiveTitle', { score: candidates?.niche_score ?? 0 })
                    : t('nicheRegime.expiredTitle')}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mt: 0.3, lineHeight: 1.5 }}>
                  {isRegimeInactive
                    ? t('nicheRegime.regimeInactiveDesc', { score: candidates?.niche_score ?? 0, date: signalDate })
                    : t('nicheRegime.expiredDesc', { days: signalAgeDays, date: signalDate })}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                {t('nicheRegime.candidatesTitle')}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                {t('nicheRegime.candidatesCount', { count: candsList.length })}
                {signalDate && <> — {t('nicheRegime.signalDate', { date: signalDate })}</>}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {Object.entries(CONFIDENCE).map(([key, tier]) => {
                const count = candsList.filter(c => (c.confidence || 'HIGH') === key).length;
                return count > 0 ? (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Chip label={`${tier.labelVi} (${count})`} size="small" sx={{
                      fontSize: '0.58rem', fontWeight: 800, height: 18,
                      bgcolor: alpha(tier.color, 0.1), color: tier.color,
                      border: `1px solid ${alpha(tier.color, 0.2)}`,
                      '& .MuiChip-label': { px: 0.6 },
                    }} />
                  </Box>
                ) : null;
              })}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <ShieldIcon sx={{ fontSize: 11, color: C.red }} />
                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>{t('nicheRegime.legendStopLoss')}</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ minWidth: 800 }}>
            <CandidateHeader t={t} />
            <Box sx={{ opacity: isExpired ? 0.45 : 1, transition: 'opacity 0.2s' }}>
              {candsList.map((c, i) => (
                <CandidateRow key={c.ticker} c={c} rank={i + 1} maxRankScore={maxRankScore} t={t} />
              ))}
            </Box>
          </Box>
        </Box>
        );
      })()}

      {/* ── History Chart — Score Timeline ── */}
      {history.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 0 } }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
              {t('nicheRegime.historyTitle')}
            </Typography>
            {historySummary && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  {t('nicheRegime.histNicheDays')}: <strong style={{ color: C.niche }}>{historySummary.niche_days}</strong>/{history.length} ({historySummary.niche_pct}%)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  {t('nicheRegime.histAvgScore')}: <strong style={{ color: C.niche }}>{historySummary.avg_niche_score}</strong>/4
                </Typography>
              </Box>
            )}
          </Box>

          {/* Score chart */}
          <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2, mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', mb: 1 }}>
              {t('nicheRegime.scoreChartTitle')}
            </Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={history} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} interval="preserveStartEnd" />
                <YAxis domain={[0, 4]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <RTooltip content={<ChartTooltip t={t} />} />
                <ReferenceLine y={4} stroke={C.niche} strokeDasharray="5 5" strokeWidth={1.5} />
                <Bar dataKey="score" radius={[2, 2, 0, 0]}>
                  {history.map((d, i) => (
                    <Cell key={i} fill={d.score >= 4 ? C.niche : d.score >= 3 ? alpha(C.niche, 0.3) : '#e2e8f0'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Dispersion chart */}
          <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', mb: 1 }}>
              {t('nicheRegime.dispersionChartTitle')}
            </Typography>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={history} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <RTooltip content={<ChartTooltip t={t} />} />
                <Area type="monotone" dataKey="disp" stroke={C.niche} fill={C.niche} fillOpacity={0.15} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}

      {/* ── Strategy Info (always show) ── */}
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>
          {t('nicheRegime.strategyTitle')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          {[
            { label: t('nicheRegime.strategyEntry'), desc: t('nicheRegime.strategyEntryDesc') },
            { label: t('nicheRegime.strategyTrailing'), desc: t('nicheRegime.strategyTrailingDesc') },
            { label: t('nicheRegime.strategyExit'), desc: t('nicheRegime.strategyExitDesc') },
            { label: t('nicheRegime.strategyBacktest'), desc: t('nicheRegime.strategyBacktestDesc') },
          ].map(({ label, desc }) => (
            <Box key={label} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha(C.niche, 0.03), border: `1px solid ${alpha(C.niche, 0.08)}` }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: C.niche, mb: 0.3 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.4 }}>{desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default NicheRegimeDashboard;
